import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, FileSpreadsheet, Download, BoxSelect, Smartphone, Users, ClipboardList, BarChart3, Trash2, Box, ShoppingCart, ArrowRightLeft, Repeat, Tag, Upload, ChevronDown, ChevronRight, Truck, AlertTriangle, Radar, Printer, PackageCheck, PackageX, Warehouse, Hourglass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { filterOperations } from '@/utils/operationAccess';

const FGWMSDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    fgBundles: 0,
    pendingPickSlips: 0,
    pickedToday: 0,
    putAwayToday: 0
  });
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const fetchStats = useCallback(async () => {
    try {
      setStats({
        fgBundles: 0,
        pendingPickSlips: 0,
        pickedToday: 0,
        putAwayToday: 0
      });
    } catch (error) {
      console.error('Error fetching FG WMS stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const moduleGroups = [
    {
      id: 'put-away',
      title: 'Put Away',
      description: 'Inward storage operations',
      color: 'border-l-purple-500',
      headerBg: 'bg-purple-50',
      modules: [
        { id: 'table-clearance', title: 'Table Clearance (Put Away)', description: 'Scan USN → Scan Bin → Place. Primary put away flow.', icon: BoxSelect, route: '/wms/fg/put-away-clearance', color: 'bg-emerald-600' },
        { id: 'put-away-supervisor', title: 'Put Away Supervisor', description: 'Manage staff and monitor put away operations', icon: Users, route: '/wms/fg/put-away-supervisor', color: 'bg-purple-500' },
        { id: 'my-put-away-tasks', title: 'GT Put Away (Scan Bundle)', description: 'Scan bundles from Goods Transfer to put away', icon: ClipboardList, route: '/wms/fg/put-away-mobile', color: 'bg-indigo-500' },
        { id: 'fob-direct-put-away', title: 'FOB Direct Put Away', description: 'Scan a FOB assortment-packed bundle to store directly in FG (skips SFG & pieces-processing)', icon: BoxSelect, route: '/wms/fg/fob-direct-put-away', color: 'bg-indigo-700' },
        { id: 'small-box-put-away', title: 'Small Box Put Away', description: 'Scan a Common-Pool small box → store the whole box in FG as one pack (auto-suggests the bin)', icon: PackageCheck, route: '/wms/fg/small-box-putaway', color: 'bg-fuchsia-600' },
        { id: 'put-away-slip-management', title: 'Legacy Slip Management', description: 'View historical put away slips (read-only)', icon: ClipboardList, route: '/wms/fg/put-away-slip-management', color: 'bg-gray-400' },
        { id: 'put-away-entries', title: 'Put Away Entries (Admin)', description: 'View and delete put away entries', icon: Trash2, route: '/wms/fg/put-away-entries', color: 'bg-red-500' },
      ]
    },
    {
      id: 'picking',
      title: 'Picking',
      description: 'Outward pick operations',
      color: 'border-l-amber-500',
      headerBg: 'bg-amber-50',
      modules: [
        { id: 'fg-pick-slips', title: 'FG Pick Slips', description: 'Import and manage pick slips from accounting', icon: FileSpreadsheet, route: '/wms/fg-pick-slips', color: 'bg-amber-500' },
        { id: 'fg-pick-slip-upload', title: 'Upload Pick Slips', description: 'Import pick slips from Excel/CSV file', icon: Upload, route: '/wms/fg/pick-slip-upload', color: 'bg-lime-500' },
        { id: 'seasonal-pick-slip-upload', title: 'Seasonal Pick Slip Upload', description: 'Auto-generate SP pick slips per slug (≤300 pcs) for pre-season packing', icon: PackageCheck, route: '/wms/seasonal/pick-slip-upload', color: 'bg-lime-600' },
        { id: 'fg-picker-guidance', title: 'FG Picker Guidance', description: 'Pick packs for accounting pick slips', icon: Download, route: '/wms/fg-picker-guidance', color: 'bg-pink-500' },
        { id: 'fg-investigation-queue', title: 'FG Investigation Queue', description: 'Items skipped during picking — supervisor review & resolve', icon: AlertTriangle, route: '/wms/fg/investigation-queue', color: 'bg-orange-500' },
        { id: 'uploaded-pick-slips-report', title: 'Uploaded Pick Slips Report', description: 'SKU-wise report of all uploaded FG pick slips (Regular + Seasonal) with upload & pick date/time (IST), user and Picked/Not-Picked — global filter + CSV', icon: ClipboardList, route: '/wms/fg/uploaded-pick-slips-report', color: 'bg-teal-600' },
      ]
    },
    {
      id: 'packing-dispatch',
      title: 'Packing & Orders',
      description: 'Pack items and manage orders',
      color: 'border-l-orange-500',
      headerBg: 'bg-orange-50',
      modules: [
        { id: 'fg-packing', title: 'FG Packing Station', description: 'Pack picked items into boxes', icon: Box, route: '/wms/fg/packing', color: 'bg-orange-500' },
        { id: 'seasonal-packing-report', title: 'Seasonal Packing Report', description: 'Per-box, per-barcode report of packed seasonal boxes with a global filter', icon: ClipboardList, route: '/wms/seasonal/packing-report', color: 'bg-lime-600' },
        { id: 'seasonal-pending-packing-report', title: 'Seasonal Pending Packing', description: 'Generated seasonal pick slips not yet packed (Not Packed / Partial) with pending qty', icon: PackageX, route: '/wms/seasonal/pending-packing-report', color: 'bg-rose-600' },
        { id: 'seasonal-bundle-wms', title: 'Seasonal Bundle WMS', description: 'Store, pick-slip & pick the seasonally packed boxes (location setup, put away, picking)', icon: Warehouse, route: '/wms/seasonal-bundle', color: 'bg-sky-700', requires_any: ['seasonal-bundle-location-setup', 'seasonal-bundle-put-away', 'seasonal-bundle-create-pick-slip', 'seasonal-bundle-available-pickers', 'seasonal-bundle-picking', 'seasonal-bundle-stock-report'] },
        { id: 'order-management', title: 'Order Management', description: 'Auto pick slip generation from sales orders', icon: ShoppingCart, route: '/wms/fg/order-management', color: 'bg-emerald-500' },
      ]
    },
    {
      id: 'storage-transfers',
      title: 'Storage & Transfers',
      description: 'Inventory, bins, and transfers',
      color: 'border-l-blue-500',
      headerBg: 'bg-blue-50',
      modules: [
        { id: 'fg-inventory-report', title: 'FG Inventory Report', description: 'View items stored in bins with qty details', icon: BarChart3, route: '/wms/fg/inventory-report', color: 'bg-cyan-500' },
        { id: 'fg-ageing-report', title: 'Stock Ageing Report', description: 'FG stock by age range (0-30 … >365 days, editable)', icon: Hourglass, route: '/reports/storage-ageing?tab=fg', color: 'bg-amber-600' },
        { id: 'footprint-capacity', title: 'Footprint Capacity', description: 'View & manage bin capacities, release bin full', icon: BoxSelect, route: '/wms/fg/footprint-capacity', color: 'bg-blue-500' },
        { id: 'bin-to-bin-transfer', title: 'Bin to Bin Transfer', description: 'Transfer USNs from one bin to another', icon: ArrowRightLeft, route: '/wms/fg/bin-transfer', color: 'bg-violet-500' },
        { id: 'bin-audit', title: 'Bin Audit Report', description: 'Find bins with multiple SKUs', icon: AlertTriangle, route: '/wms/fg/bin-audit', color: 'bg-amber-500' },
        { id: 'fg-design-transfer', title: 'Design Transfer', description: 'Transfer USNs from one SKU to another', icon: Repeat, route: '/wms/fg/design-transfer', color: 'bg-pink-500' },
        { id: 'fg-design-transfer-report', title: 'Design Transfer Report', description: 'View transfer history with CSV export', icon: FileSpreadsheet, route: '/wms/fg/design-transfer-report', color: 'bg-rose-500' },
        { id: 'design-transfer-slips', title: 'Design Transfer Slips', description: 'Import & manage design transfer pick slips', icon: ArrowRightLeft, route: '/wms/fg/design-transfer-slips', color: 'bg-indigo-500' },
        { id: 'usn-label-reprint', title: 'USN Label Reprint', description: 'Scan existing USN to reprint label', icon: Tag, route: '/wms/fg/usn-label-reprint', color: 'bg-teal-500' },
        { id: 'bulk-resticker', title: 'Bulk Resticker', description: 'Bulk relabel from staged bins — instant 50k+ label printing', icon: Printer, route: '/wms/fg/bulk-resticker', color: 'bg-orange-500' },
        { id: 'wms-storage-transfer', title: 'WMS Storage Transfer', description: 'Transfer FG/SFG items to other sections', icon: Truck, route: '/wms/fg/storage-transfer', color: 'bg-amber-600' },
      ]
    },
    {
      id: 'combo',
      title: 'Combo Products',
      description: 'Assemble and manage combos',
      color: 'border-l-fuchsia-500',
      headerBg: 'bg-fuchsia-50',
      modules: [
        { id: 'combo-bom', title: 'Combo BOM Master', description: 'Manage combo product definitions', icon: Package, route: '/wms/combo/bom', color: 'bg-fuchsia-500' },
        { id: 'combo-assembly', title: 'Combo Assembly Station', description: 'Assemble combo products from components', icon: BoxSelect, route: '/wms/combo/assembly', color: 'bg-purple-500' },
        { id: 'combo-v2', title: 'Combo V2 — Bulk Assembly', description: 'Matrix planner + distributor + PP scan flow', icon: BoxSelect, route: '/wms/combo-v2', color: 'bg-emerald-500',
          requires_any: ['combo-v2-dashboard', 'combo-v2-planner', 'combo-v2-plans', 'combo-v2-distributor', 'combo-v2-pp-station', 'combo-v2-return-excess', 'combo-v2-reversal', 'combo-successful-sessions', 'combo-floor-reconciliation', 'combo-pp-staff-performance'] },
        { id: 'combo-reversal', title: 'Combo Reversal', description: 'Reverse/disassemble combo products', icon: Repeat, route: '/wms/combo/reversal', color: 'bg-rose-500' },
        { id: 'combo-bundle-trace', title: 'Bundle Trace', description: 'Full history of a bundle — picker, handover, sessions, leftover, live balance', icon: Radar, route: '/wms/combo/bundle-trace', color: 'bg-indigo-500' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="fg-wms-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">FG WMS</h1>
                <p className="text-sm text-[#E8DCC8]">Finished Goods Warehouse</p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate('/wms/mobile')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="fg-wms-mobile-view-btn"
          >
            <Smartphone className="w-4 h-4 mr-2" />
            Mobile View
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">FG Bundles in Storage</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.fgBundles}</p>
              </div>
              <Package className="w-10 h-10 text-teal-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Pending Pick Slips</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.pendingPickSlips}</p>
              </div>
              <FileSpreadsheet className="w-10 h-10 text-amber-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Picked Today</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.pickedToday}</p>
              </div>
              <Download className="w-10 h-10 text-pink-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Put Away Today</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.putAwayToday}</p>
              </div>
              <BoxSelect className="w-10 h-10 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Grouped Modules */}
        <div className="space-y-6">
          {moduleGroups.map((group) => {
            const filteredModules = filterOperations('fg_wms', group.modules);
            if (filteredModules.length === 0) return null;
            const isCollapsed = collapsedGroups[group.id];
            return (
              <div key={group.id} className={`bg-white rounded-xl border border-[#E5DFD0] overflow-hidden border-l-4 ${group.color}`} data-testid={`fg-group-${group.id}`}>
                {/* Group Header - Clickable to collapse */}
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`w-full flex items-center justify-between px-6 py-4 ${group.headerBg} hover:brightness-95 transition-all`}
                  data-testid={`fg-group-toggle-${group.id}`}
                >
                  <div className="flex items-center gap-3">
                    {isCollapsed ? (
                      <ChevronRight className="w-5 h-5 text-[#5A5F6E]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#5A5F6E]" />
                    )}
                    <div className="text-left">
                      <h2 className="text-lg font-bold text-[#1A1F2E] font-display">{group.title}</h2>
                      <p className="text-xs text-[#5A5F6E]">{group.description}</p>
                    </div>
                  </div>
                  <span className="text-sm text-[#5A5F6E] bg-white/60 px-3 py-1 rounded-full">
                    {filteredModules.length} items
                  </span>
                </button>

                {/* Group Content */}
                {!isCollapsed && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredModules.map((module) => {
                        const Icon = module.icon;
                        return (
                          <div
                            key={module.id}
                            onClick={() => navigate(module.route)}
                            className="flex items-start gap-4 p-4 rounded-lg border border-[#E5DFD0] hover:border-[#2D5F4F] hover:shadow-md transition-all duration-200 cursor-pointer bg-white"
                            data-testid={`fg-wms-module-${module.id}`}
                          >
                            <div className={`${module.color} p-3 rounded-lg shrink-0`}>
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-base font-semibold text-[#1A1F2E] font-display leading-tight">
                                {module.title}
                              </h3>
                              <p className="text-xs text-[#5A5F6E] mt-1 leading-relaxed">{module.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default FGWMSDashboard;
